import React, { useState } from 'react';
import { LoadingButton } from '@lf/ui-primitives';
import FeedbackButton from '../../components/FeedbackButton';
import SaveHeartButton from '../../components/SaveHeartButton';
import VideoList from '../../components/VideoList';
import SearchBox from '../../components/SearchBox';
import { LoadingIndicator } from '@lf/ui-primitives';
import RecommendedPath from './RecommendedPath';
import { useError } from '../../contexts/ErrorContext';
import { collectRecommendedVideoIds } from '../../lib/videos/collectRecommendedVideoIds';
import { filterRecommendedPathByTopic } from '../../lib/videos/filterRecommendedPathByTopic';
import { useSavedVideoList } from '../../lib/videos/useSavedVideoList';
import { stripEmoji } from '../../lib/utils/stripEmoji';

function _progressStyle(videoId, progressDict) {
    const progress = progressDict[videoId];
    if (progress === undefined) {
        return 'hidden';
    } else if (progress === 100) {
        return 'w-full';
    } else {
        const progressStyle = 'w-' + Math.max(1, Math.floor(progress / 20)).toString() + '/5';
        return progressStyle;
    }
}

function _renderVideo(feedItems, video, reportedVideos, onVideoSelect, isSaved, onSaveToggle) {
    const videoId = video.id.videoId;
    const { title, thumbnails, channelTitle, channelId } = video.snippet;

    // Click in the All Videos grid emits source=all_videos so completion
    // and engagement reports can split rec_path vs all_videos cleanly,
    // even though both grids fire from the same /feed page.
    const handleSelect = () => onVideoSelect(
        video,
        feedItems.study_plan_id,
        feedItems.current_position,
        { source: 'all_videos' },
    );

    return (
        <div key={videoId} className="h-full flex flex-col border rounded-2xl p-3 relative !bg-white">
            {/* Thumbnail */}
            <div className="relative aspect-video rounded-xl overflow-hidden flex items-center justify-center bg-black">
                <img
                    src={thumbnails.high.url}
                    alt={title}
                    className="w-full cursor-pointer object-fill"
                    onClick={handleSelect}
                />
                <div className={`z-10 absolute bottom-0 left-0 h-1 bg-red-500 ${_progressStyle(videoId, feedItems.progress_by_video_id)}`}></div>
            </div>

            {/* Title */}
            <div className="flex-1 mt-4">
                <h4 className="font-bold cursor-pointer text-sm" onClick={handleSelect}>
                    {title}
                </h4>
                <p className="text-xs mt-1">{channelTitle}</p>
            </div>

            <div className="absolute bottom-0 right-0 mb-2 mr-2 flex gap-1">
                <SaveHeartButton isSaved={isSaved} onClick={() => onSaveToggle(video)} />
                <FeedbackButton contentIds={[videoId, channelId]} data={{ lesson_id: feedItems.lesson_id }} />
            </div>
        </div>
    );
}

function _normalizeConcept(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function _renderConceptTokens(concepts, matchingSet) {
    if (!concepts || concepts.length === 0) {
        return <span className="text-gray-500">No concept mapping available.</span>;
    }
    return (
        <div className="flex flex-wrap gap-1 mt-1">
            {concepts.map((concept) => {
                const isMatch = matchingSet.has(_normalizeConcept(concept));
                return (
                    <span
                        key={`${concept}-${isMatch ? 'match' : 'other'}`}
                        className={`inline-flex items-center rounded px-1.5 py-0.5 ${isMatch ? 'bg-green-100 text-green-900' : 'bg-gray-100 text-gray-700'}`}
                    >
                        {isMatch ? '✓' : '•'} {concept}
                    </span>
                );
            })}
        </div>
    );
}

const Feed = ({
    videos: feed,
    recommendedPath,
    isRecommendedPathLoading = false,
    isItemsLoading = false,
    debugMode = false,
    debugFeedData = null,
    onVideoSelect,
    onStudyPlanSelect,
    onNext,
    onNextTopic,
    onPrev,
}) => {
    const [videoCount, setVideoCount] = useState(6);
    const [reportedVideos] = useState({});
    const [isSearchVisible, setIsSearchVisible] = useState(false);

    const { setError } = useError();

    const { savedVideoIds, handleSaveToggle } = useSavedVideoList({ setError });

    const DEFAULT_VALUE = 6;

    if (!feed) {
        return <div className="mt-12"><LoadingIndicator /></div>;
    }

    const onShowMore = () => {
        setVideoCount(videoCount + DEFAULT_VALUE);
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSearchClick = () => {
        setIsSearchVisible(true);
        scrollToTop();
    };

    const renderedTopicVideos = feed.map((feedItems, feedIndex) => {
        const isRecommendedStillLoading = feedIndex === 0 && (
            isRecommendedPathLoading ||
            recommendedPath?.status === 'pending' ||
            recommendedPath?.status === 'computing'
        );
        // Lesson-keyed rec scoped to the active topic for the "Recommended"
        // grid. The unfiltered path is still used for the "All Videos"
        // exclude set so any lesson-rec'd video the topic feed surfaces
        // doesn't render twice.
        const topicRecommendedPath = filterRecommendedPathByTopic(
            recommendedPath,
            feedItems?.current_position?.topic_id,
        );
        const recommendedVideoIds = collectRecommendedVideoIds(
            feedIndex, recommendedPath, feedItems,
        );
        const allVideoItems = recommendedVideoIds
            ? feedItems.items.filter((v) => !recommendedVideoIds.has(v.id.videoId))
            : feedItems.items;
        const allDebugVideoItems = feedItems.items || [];
        const allDebugByVideoId = Object.fromEntries(
            (debugFeedData?.all_videos || []).map((entry) => [entry.video_id, entry]),
        );
        const recommendedDebugByVideoId = Object.fromEntries(
            (debugFeedData?.recommended_videos || []).map((entry) => [entry.video_id, entry]),
        );
        const topicConcepts = debugFeedData?.topic_concepts || [];

        return <div key={feedIndex}>
            <div className="flex justify-end">
                <button
                    onClick={() => onStudyPlanSelect(feedItems.study_plan_id)}
                    className="bg-primary text-white py-2 px-4 rounded mb-2 sm:mb-0"
                >
                    View study plan
                </button>
            </div>

            <div className="mb-1 cursor-pointer" onClick={() => onStudyPlanSelect(feedItems.study_plan_id)}>
                {feedItems.current_position?.lesson_index != null && feedItems.topic && (
                    <p className="text-sm text-gray-500 font-medium uppercase">
                        Lesson {feedItems.current_position.lesson_index + 1}: {stripEmoji(feedItems.topic)}
                    </p>
                )}
                <h2 className="text-4xl mt-2 font-bold text-gray-900">
                    {stripEmoji(feedItems.topic_title || feedItems.topic)}
                </h2>
            </div>

            <div className="mb-4 flex flex-col gap-2">
                {
                    feedItems.completed_video_ids.length >= feedItems.lesson_num_videos && !isSearchVisible &&
                    <div
                        data-testid="feed-next-lesson-banner"
                        className="mt-8 bg-green-50 border border-secondary rounded-xl p-4 flex items-center justify-between gap-4 w-full"
                    >
                        <span className="text-secondary-dark font-semibold">
                            Great job — you finished all recommended videos.
                        </span>
                        <LoadingButton onClick={() => onNext(feedItems.study_plan_id, true)} text="Next lesson" />
                    </div>
                }
                {isSearchVisible && (
                    <div>
                        <SearchBox isSearchVisible={isSearchVisible} setSearchVisible={setIsSearchVisible} />
                    </div>
                )}
            </div>

            {debugMode && (
                <div className="mb-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-xs">
                    <h3 className="font-semibold text-gray-700">Debug mode: concept coverage</h3>
                    <div className="mt-2">
                        <p className="font-medium text-gray-700">Topic concepts</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {topicConcepts.length > 0 ? topicConcepts.map((concept) => (
                                <span key={concept} className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-900">
                                    {concept}
                                </span>
                            )) : (
                                <span className="text-gray-500">No topic concepts available.</span>
                            )}
                        </div>
                    </div>

                    {feedIndex === 0 && (
                        <div className="mt-3">
                            <p className="font-medium text-gray-700">Recommended videos</p>
                            <div className="mt-1 space-y-2">
                                {(recommendedPath?.recommended_videos || []).length > 0 ? (
                                    (recommendedPath?.recommended_videos || []).map((video) => {
                                        const debugEntry = recommendedDebugByVideoId[video.video_id] || {};
                                        const matchingSet = new Set(
                                            (debugEntry.matching_topic_concepts || []).map(_normalizeConcept),
                                        );
                                        return (
                                            <div key={`debug-rec-${video.video_id}`} className="rounded border border-gray-200 bg-white p-2">
                                                <p className="font-medium text-gray-800">{video.title}</p>
                                                {_renderConceptTokens(debugEntry.concepts_taught || [], matchingSet)}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p className="text-gray-500">No recommended videos available.</p>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="mt-3">
                        <p className="font-medium text-gray-700">All videos</p>
                        <div className="mt-1 space-y-2 max-h-56 overflow-auto pr-1">
                            {allDebugVideoItems.length > 0 ? allDebugVideoItems.map((video) => {
                                const videoId = video.id.videoId;
                                const debugEntry = allDebugByVideoId[videoId] || {};
                                const matchingSet = new Set(
                                    (debugEntry.matching_topic_concepts || []).map(_normalizeConcept),
                                );
                                return (
                                    <div key={`debug-all-${videoId}`} className="rounded border border-gray-200 bg-white p-2">
                                        <p className="font-medium text-gray-800">{video.snippet.title}</p>
                                        {_renderConceptTokens(debugEntry.concepts_taught || [], matchingSet)}
                                    </div>
                                );
                            }) : (
                                <p className="text-gray-500">No videos available.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Recommended Path Section */}
            {feedIndex === 0 && (
                <RecommendedPath
                    recommendedPath={topicRecommendedPath}
                    feedItems={feedItems}
                    isLastTopic={feedItems.current_position?.topic_index === feedItems.lesson_topics_count - 1}
                    onVideoSelect={onVideoSelect}
                    savedVideoIds={savedVideoIds}
                    onSaveToggle={handleSaveToggle}
                    onNext={onNext}
                    onNextTopic={onNextTopic}
                    isItemsLoading={isItemsLoading}
                    isLoading={isRecommendedStillLoading}
                />
            )}

            <div>
                {feedIndex === 0 && (isItemsLoading || isRecommendedStillLoading || (recommendedPath && recommendedPath.status !== 'error')) && (
                    <h3 className="text-2xl font-bold mt-12 mb-3 text-primary-dark">All Videos</h3>
                )}
                {isItemsLoading ? (
                    <div className="flex justify-center my-20"><LoadingIndicator /></div>
                ) : (
                    <>
                        <VideoList name={feedItems.study_plan_id}>
                            {allVideoItems.slice(0, videoCount).map((video) =>
                                <div key={video.id.videoId} className="h-full">
                                    {
                                        _renderVideo(
                                            feedItems,
                                            video,
                                            reportedVideos,
                                            onVideoSelect,
                                            savedVideoIds.has(video.id.videoId),
                                            handleSaveToggle,
                                        )
                                    }
                                </div>
                            )}
                        </VideoList>
                        {videoCount < allVideoItems.length ? (
                            <div className="flex justify-center mt-6">
                                <button
                                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                                    onClick={onShowMore}
                                >
                                    Show more
                                </button>
                            </div>
                        ) : (allVideoItems.length > 0 ? (
                            <div className="flex justify-center mt-12 text-gray-600">
                                Don't see any interesting videos to watch? Try{' '}
                                <span
                                    className="text-blue-500 cursor-pointer underline ml-1"
                                    onClick={handleSearchClick}
                                >
                                    searching
                                </span>
                                .
                            </div>
                        ) : (isSearchVisible ? (
                            <div className="flex justify-center my-20 text-gray-600">
                                No relevant videos found. Please try searching for something else.
                            </div>
                        ) : (isRecommendedStillLoading ? (
                            <div className="flex justify-center my-20 text-gray-600">
                                Preparing recommended videos...
                            </div>
                        ) : (
                            <div className="flex flex-col my-20 gap-8">
                                <div className="flex justify-center text-gray-600 items-center">
                                    No videos found. Please try again later.
                                </div>
                                <div className="flex justify-center text-gray-600 items-center gap-2">
                                    Would you like to proceed to the next lesson?
                                    <LoadingButton onClick={() => onNext(feedItems.study_plan_id, false)} text="Next lesson" />
                                </div>
                            </div>
                        ))))}
                    </>
                )}
            </div>
            <div className="flex justify-center mt-6">
                <div className="w-[266px] h-[66px] overflow-hidden">
                    <img src="https://developers.google.com/static/youtube/images/developed-with-youtube-logos-2x.png"
                        alt="Developed with YouTube" className="w-[266px] h-auto" />
                </div>
            </div>
        </div>
    });

    return <div className="text-left">
        {renderedTopicVideos}
    </div>;
};

export default Feed;
